/**
 * NoteTagsSection — tag-urile unei notițe (afișare + editare).
 * Paritate cu CredentialTagsSection din VaultDetail. Tag-urile sunt metadate
 * în clar, per tenant; conținutul notiței rămâne criptat.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import type { NoteDetail } from '@/api/notes';
import { tagsApi } from '@/api/tags';
import TagPicker from '@/components/ui/TagPicker';
import TagPill from '@/components/ui/TagPill';

interface Props {
  note: NoteDetail;
  tenantId: string;
}

export default function NoteTagsSection({ note, tenantId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const tagsList = useQuery({
    queryKey: ['tags', tenantId],
    queryFn: () => tagsApi.list(tenantId),
  });

  const attached = new Set(note.tag_ids);

  const setMutation = useMutation({
    mutationFn: (tagIds: string[]) => tagsApi.setNoteTags(note.id, tagIds),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['note', note.id] });
      queryClient.invalidateQueries({ queryKey: ['notes', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tags', tenantId] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.detail : t('notes.tags_failed'));
    },
  });

  const attachedTags = (tagsList.data ?? []).filter((tag) => attached.has(tag.id));

  return (
    <section className="pt-4 mt-4 border-t border-[var(--border)]" data-testid="note-tags">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="section-label no-dot">{t('notes.tags_title')}</h3>
        <TagPicker
          tenantId={tenantId}
          selected={attached}
          onChange={(next) => setMutation.mutate(Array.from(next))}
        />
      </div>
      {attachedTags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {attachedTags.map((tag) => (
            <TagPill
              key={tag.id}
              label={tag.name}
              color={tag.color ?? 'neutral'}
              onRemove={() => {
                const next = new Set(attached);
                next.delete(tag.id);
                setMutation.mutate(Array.from(next));
              }}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs" style={{ color: 'var(--text-dim)' }}>
          {t('notes.tags_empty')}
        </p>
      )}
      {error && (
        <p className="text-xs mt-2" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}
    </section>
  );
}
